/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MessageComposer.tsx - Premium Editor con Template & Variables
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @version 2.0.0
 * @description Editor completo con toolbar, templates, variables y preview
 * @architecture Fusión de BulkSend + Scheduler
 * 
 * FEATURES:
 * - ✅ Toolbar completo (Bold, Italic, Code, Link, Emoji)
 * - ✅ Template selector con API real
 * - ✅ Variable management modal (enable/disable)
 * - ✅ Undo/Redo history
 * - ✅ Preview WhatsApp en tiempo real
 * - ✅ RiskIndicator tipo lightsaber
 * - ✅ Sistema de notificaciones
 * - ✅ Stats avanzadas (caracteres, emojis, links)
 * - ✅ Dark/Light mode compatible
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { 
  Card, 
  Form, 
  Button, 
  Badge, 
  Row, 
  Col, 
  ButtonGroup, 
  Modal, 
  Spinner 
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen,
  faPlus,
  faSave,
  faUndo,
  faRedo,
  faFileAlt,
  faSmile,
  faLink,
  faExclamationTriangle,
  faCog,
  faTimes,
  faLock,
  faBold,
  faItalic,
  faStrikethrough,
  faCode,
  faCheck,
  faTag,
  faClock,
  faChartLine,
  faCheckCircle,
  faInfoCircle,
  faLightbulb,
  faMessage
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

// Types
import type { RiskData, RiskLevel } from '../../../types/scheduler-shared.types';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════════════

export interface MessageVariable {
  id: string;
  name: string;
  placeholder: string;
  example: string;
  required: boolean;
  type: 'text' | 'number' | 'date' | 'currency';
}

export interface MessageTemplate {
  id: string;
  phoneId: string;
  name: string;
  category: string;
  content: string;
  variables: string[];
  metadata: {
    createdAt: string;
    updatedAt: string;
    usageCount: number;
    lastUsed: string | null;
  };
}

export interface MessageStats {
  characterCount: number;
  emojiCount: number;
  linkCount: number;
  variableCount: number;
  estimatedMessages: number;
}

export interface MessageComposerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  riskData?: RiskData | null;
  showRiskIndicator?: boolean;
  showPreview?: boolean;
  showTemplates?: boolean;
  contactCount?: number;
  sampleContact?: any;
  error?: string;
  disabled?: boolean;
  className?: string;
  onStatsChange?: (stats: MessageStats) => void;
  phoneId?: string; // NUEVO: phoneId del bot
  apiToken?: string; // NUEVO: token de autorización
}

interface CustomVariable extends MessageVariable {
  enabled: boolean;
  locked: boolean;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const DEFAULT_VARIABLES: MessageVariable[] = [
  {
    id: 'nombre',
    name: 'Nombre',
    placeholder: '[nombre]',
    example: 'Juan Pérez',
    required: true,
    type: 'text'
  },
  {
    id: 'telefono',
    name: 'Teléfono',
    placeholder: '[telefono]',
    example: '+56912345678',
    required: false,
    type: 'text'
  },
  {
    id: 'curso',
    name: 'Curso',
    placeholder: '[curso]',
    example: 'React Avanzado',
    required: false,
    type: 'text'
  },
  {
    id: 'email',
    name: 'Email',
    placeholder: '[email]',
    example: 'juan@email.com',
    required: false,
    type: 'text'
  },
  {
    id: 'empresa',
    name: 'Empresa',
    placeholder: '[empresa]',
    example: 'Mi Empresa',
    required: false,
    type: 'text'
  },
  {
    id: 'fecha',
    name: 'Fecha',
    placeholder: '[fecha]',
    example: '15/02/2025',
    required: false,
    type: 'date'
  }
];

const SYSTEM_VARIABLES = ['nombre', 'email']; // Variables bloqueadas

const RISK_COLORS: Record<RiskLevel, string> = {
  low: '#00BDA5',
  medium: '#FFB84D',
  high: '#FF7A59',
  critical: '#F2545B'
};

const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'Bajo',
  medium: 'Medio',
  high: 'Alto',
  critical: 'Crítico'
};

const RISK_ICONS: Record<RiskLevel, any> = {
  low: faCheckCircle,
  medium: faInfoCircle,
  high: faExclamationTriangle,
  critical: faExclamationTriangle
};

const EMOJI_LIST = ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '👍', '👎', '👏', '🙌', '🤝', '🙏', '💪', '✊', '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '🔥', '✨', '⭐', '🌟', '💫', '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🥈', '🥉', '⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱', '🏓', '🏸', '🥅', '🎯', '⛳', '💻', '📱', '☎️', '📞', '📧', '✉️', '📨', '📩', '📮', '📫', '📪', '📬', '📭', '📦', '📄', '📃', '📑', '📊', '📈', '📉', '💰', '💵', '💴', '💶', '💷', '💳', '🏦', '🏧'];

// API Constants
const API_BASE_URL = 'https://backend.anubisai.net/api/whatsapp/wasp';
const API_KEY = 'anubis-whatsapp-api-key';

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

const getInitialVariables = (): CustomVariable[] => {
  const saved = localStorage.getItem('scheduler.customVariables');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      console.error('Error loading custom variables:', e);
    }
  }
  
  return DEFAULT_VARIABLES.map(v => ({
    ...v,
    enabled: true,
    locked: SYSTEM_VARIABLES.includes(v.id)
  }));
};

const saveCustomVariables = (variables: CustomVariable[]) => {
  localStorage.setItem('scheduler.customVariables', JSON.stringify(variables));
};

// Get API token from localStorage
const getApiToken = () => {
  try {
    const hatateToken = localStorage.getItem('hatateToken');
    return hatateToken || '';
  } catch (e) {
    console.error('Error getting API token:', e);
    return '';
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export const MessageComposer: React.FC<MessageComposerProps> = ({
  value,
  onChange,
  placeholder = 'Escribe tu mensaje aquí...',
  maxLength = 1000,
  minLength = 10,
  riskData,
  showRiskIndicator = true,
  showPreview = true,
  showTemplates = true,
  contactCount = 0,
  sampleContact,
  error,
  disabled = false,
  className = '',
  onStatsChange,
  phoneId: phoneIdProp,
  apiToken: apiTokenProp
}) => {
  
  // ───────────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────────
  
  const [message, setMessage] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [history, setHistory] = useState<string[]>([value]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // Modals
  const [showVariableModal, setShowVariableModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  
  // Variables
  const [customVariables, setCustomVariables] = useState<CustomVariable[]>(getInitialVariables);
  
  // Templates
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [modalZIndex, setModalZIndex] = useState(999999);
  
  // Link modal
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Usar phoneId y apiToken de props o fallback a localStorage
  const phoneId = phoneIdProp || '';
  const apiToken = apiTokenProp || getApiToken();
  
  // ───────────────────────────────────────────────────────────────────────
  // COMPUTED
  // ───────────────────────────────────────────────────────────────────────
  
  const stats = useMemo((): MessageStats => {
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu;
    const linkRegex = /(https?:\/\/[^\s]+)/g;
    const variableRegex = /\[[\w]+\]/g;
    
    const emojiCount = (message.match(emojiRegex) || []).length;
    const linkCount = (message.match(linkRegex) || []).length;
    const variableCount = (message.match(variableRegex) || []).length;
    const estimatedMessages = Math.ceil(message.length / 160);
    
    return {
      characterCount: message.length,
      emojiCount,
      linkCount,
      variableCount,
      estimatedMessages
    };
  }, [message]);
  
  const charPercentage = (stats.characterCount / maxLength) * 100;
  const isValid = stats.characterCount >= minLength && stats.characterCount <= maxLength;
  const isTooShort = stats.characterCount > 0 && stats.characterCount < minLength;
  const isTooLong = stats.characterCount > maxLength;
  
  // Variables habilitadas
  const enabledVariables = useMemo(() => {
    return customVariables.filter(v => v.enabled);
  }, [customVariables]);
  
  // Variables usadas en el mensaje
  const usedVariables = useMemo(() => {
    return enabledVariables.filter(v => message.includes(v.placeholder));
  }, [message, enabledVariables]);
  
  // Preview con variables reemplazadas
  const previewMessage = useMemo(() => {
    let preview = message;
    enabledVariables.forEach(v => {
      const example = sampleContact?.[v.id] || v.example;
      preview = preview.replace(new RegExp(v.placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), example);
    });
    return preview;
  }, [message, enabledVariables, sampleContact]);
  
  // ───────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────────
  
  // Sync con props
  useEffect(() => {
    if (value !== message) {
      setMessage(value);
    }
  }, [value]);
  
  // Sync stats
  useEffect(() => {
    onStatsChange?.(stats);
  }, [stats, onStatsChange]);
  
  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);
  
  // Cargar template pendiente
  useEffect(() => {
    const pending = localStorage.getItem('scheduler:pendingTemplate');
    if (pending) {
      try {
        const template = JSON.parse(pending);
        handleMessageChange(template.content);
        
        // Sincronizar variables
        const templateVars = template.variables || [];
        setCustomVariables(prev => {
          const updated = [...prev];
          templateVars.forEach((varName: string) => {
            const varIndex = updated.findIndex(v => v.id === varName);
            if (varIndex !== -1) {
              updated[varIndex] = { ...updated[varIndex], enabled: true };
            }
          });
          saveCustomVariables(updated);
          return updated;
        });
        
        showNotification('success', `✅ Template "${template.name}" cargado`);
        localStorage.removeItem('scheduler:pendingTemplate');
      } catch (e) {
        console.error('Error loading pending template:', e);
      }
    }
  }, []);
  
  // ───────────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────────
  
  const handleMessageChange = (newValue: string) => {
    setMessage(newValue);
    onChange(newValue);
    
    // Update history
    if (newValue !== history[historyIndex]) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newValue);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  };
  
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const newValue = history[newIndex];
      setMessage(newValue);
      onChange(newValue);
    }
  };
  
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const newValue = history[newIndex];
      setMessage(newValue);
      onChange(newValue);
    }
  };
  
  const insertAtCursor = (text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const newValue = message.substring(0, start) + text + message.substring(end);
    
    handleMessageChange(newValue);
    
    setTimeout(() => {
      textarea.focus();
      const newPos = start + text.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };
  
  const handleInsertVariable = (variable: CustomVariable) => {
    insertAtCursor(variable.placeholder);
    setShowVariableModal(false);
  };
  
  const handleInsertEmoji = (emoji: string) => {
    insertAtCursor(emoji);
    setShowEmojiPicker(false);
  };
  
  const handleInsertLink = () => {
    if (!linkUrl) return;
    
    const linkMd = linkText ? `[${linkText}](${linkUrl})` : linkUrl;
    insertAtCursor(linkMd);
    
    setLinkText('');
    setLinkUrl('');
    setShowLinkModal(false);
    
    showNotification('success', '✅ Link insertado');
  };
  
  const handleFormatText = (format: 'bold' | 'italic' | 'code') => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = message.substring(start, end);
    
    if (!selectedText) {
      showNotification('warning', '⚠️ Selecciona texto primero');
      return;
    }
    
    let wrapper = '';
    switch (format) {
      case 'bold':
        wrapper = '*';
        break;
      case 'italic':
        wrapper = '_';
        break;
      case 'code':
        wrapper = '`';
        break;
    }
    
    const newValue = message.substring(0, start) + wrapper + selectedText + wrapper + message.substring(end);
    handleMessageChange(newValue);
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, end + (wrapper.length * 2));
    }, 0);
  };
  
  const showNotification = (type: Notification['type'], msg: string) => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, type, message: msg }]);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  };
  
  const bringModalToFront = () => {
    setModalZIndex(prev => prev + 1);
  };
  
  const loadTemplates = async () => {
    if (!phoneId) return;
    
    try {
      setLoadingTemplates(true);
      
      const response = await axios.get(`${API_BASE_URL}/templates`, {
        params: { phoneId },
        headers: {
          'x-api-key': API_KEY,
          'Authorization': apiToken
        }
      });
      
      if (response.data.success) {
        const validTemplates = (response.data.templates || []).filter((t: MessageTemplate) => 
          t && 
          t.content && 
          t.content.trim() !== '' &&
          t.name &&
          t.name.trim() !== ''
        );
        setTemplates(validTemplates);
      }
    } catch (error: any) {
      showNotification('error', '❌ Error al cargar templates: ' + error.message);
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  const loadCategories = async () => {
    if (!phoneId) return;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/templates-categories`, {
        params: { phoneId },
        headers: {
          'x-api-key': API_KEY,
          'Authorization': apiToken
        }
      });
      
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error: any) {
      console.error('Error loading categories:', error);
    }
  };
  
  const trackTemplateUsage = async (templateId: string) => {
    try {
      await axios.post(`${API_BASE_URL}/templates/${templateId}/use`, {}, {
        headers: {
          'x-api-key': API_KEY,
          'Authorization': apiToken
        }
      });
    } catch (error: any) {
      console.error('Error tracking usage:', error);
    }
  };
  
  const handleOpenTemplateSelector = () => {
    if (!phoneId) {
      showNotification('error', '❌ Esperando conexión con el servidor...');
      return;
    }
    setShowTemplateModal(true);
    bringModalToFront();
    loadTemplates();
    loadCategories();
  };
  
  const handleLoadTemplate = (template: MessageTemplate) => {
    handleMessageChange(template.content);
    
    // Sincronizar variables
    const templateVars = template.variables;
    setCustomVariables(prev => {
      const updated = [...prev];
      templateVars.forEach(varName => {
        const varIndex = updated.findIndex(v => v.id === varName);
        if (varIndex !== -1) {
          updated[varIndex] = { ...updated[varIndex], enabled: true };
        }
      });
      saveCustomVariables(updated);
      return updated;
    });
    
    trackTemplateUsage(template.id);
    setShowTemplateModal(false);
    showNotification('success', `✅ Template "${template.name}" cargado`);
  };
  
  const filteredTemplates = selectedCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: RISK INDICATOR
  // ───────────────────────────────────────────────────────────────────────
  
  const renderRiskIndicator = () => {
    if (!showRiskIndicator || !riskData) return null;
    
    const color = RISK_COLORS[riskData.level];
    const label = RISK_LABELS[riskData.level];
    const icon = RISK_ICONS[riskData.level];
    
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        borderRadius: '20px',
        backgroundColor: `${color}15`,
        border: `1px solid ${color}40`,
        transition: 'all 0.3s ease'
      }}>
        <FontAwesomeIcon 
          icon={icon} 
          style={{ 
            color, 
            fontSize: '0.875rem'
          }} 
        />
        <div style={{
          fontSize: '0.813rem',
          fontWeight: 600,
          color
        }}>
          Riesgo {label}
        </div>
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          backgroundColor: color,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.75rem',
          fontWeight: 700
        }}>
          {riskData.score}
        </div>
      </div>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: TOOLBAR
  // ───────────────────────────────────────────────────────────────────────
  
  const renderToolbar = () => {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
        paddingBottom: '12px',
        borderBottom: '1px solid var(--bs-border-color)',
        flexWrap: 'wrap'
      }}>
        {/* Format buttons */}
        <ButtonGroup size="sm">
          <Button
            variant="outline-secondary"
            onClick={() => handleFormatText('bold')}
            disabled={disabled}
            style={{ borderRadius: '6px 0 0 6px' }}
            title="Negrita (*texto*)"
          >
            <FontAwesomeIcon icon={faBold} />
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => handleFormatText('italic')}
            disabled={disabled}
            title="Cursiva (_texto_)"
          >
            <FontAwesomeIcon icon={faItalic} />
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => handleFormatText('code')}
            disabled={disabled}
            style={{ borderRadius: '0 6px 6px 0' }}
            title="Código (`texto`)"
          >
            <FontAwesomeIcon icon={faCode} />
          </Button>
        </ButtonGroup>
        
        {/* Insert buttons */}
        <ButtonGroup size="sm">
          <Button
            variant="outline-secondary"
            onClick={() => setShowVariableModal(true)}
            disabled={disabled}
            style={{ borderRadius: '6px 0 0 6px' }}
            title="Insertar variable"
          >
            <FontAwesomeIcon icon={faLightbulb} className="me-1" />
            Variables
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={disabled}
            title="Insertar emoji"
          >
            <FontAwesomeIcon icon={faSmile} />
          </Button>
          <Button
            variant="outline-secondary"
            onClick={() => setShowLinkModal(true)}
            disabled={disabled}
            style={{ borderRadius: '0 6px 6px 0' }}
            title="Insertar link"
          >
            <FontAwesomeIcon icon={faLink} />
          </Button>
        </ButtonGroup>
        
        {/* History buttons */}
        <ButtonGroup size="sm">
          <Button
            variant="outline-secondary"
            onClick={handleUndo}
            disabled={disabled || historyIndex <= 0}
            style={{ borderRadius: '6px 0 0 6px' }}
            title="Deshacer"
          >
            <FontAwesomeIcon icon={faUndo} />
          </Button>
          <Button
            variant="outline-secondary"
            onClick={handleRedo}
            disabled={disabled || historyIndex >= history.length - 1}
            style={{ borderRadius: '0 6px 6px 0' }}
            title="Rehacer"
          >
            <FontAwesomeIcon icon={faRedo} />
          </Button>
        </ButtonGroup>
        
        {/* Stats */}
        <div style={{
          marginLeft: 'auto',
          display: 'flex',
          gap: '12px',
          fontSize: '0.75rem',
          color: 'var(--bs-secondary)'
        }}>
          {stats.emojiCount > 0 && (
            <span title="Emojis">
              <FontAwesomeIcon icon={faSmile} className="me-1" />
              {stats.emojiCount}
            </span>
          )}
          {stats.linkCount > 0 && (
            <span title="Links">
              <FontAwesomeIcon icon={faLink} className="me-1" />
              {stats.linkCount}
            </span>
          )}
          {stats.variableCount > 0 && (
            <span title="Variables">
              <FontAwesomeIcon icon={faLightbulb} className="me-1" />
              {stats.variableCount}
            </span>
          )}
        </div>
      </div>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: EDITOR
  // ───────────────────────────────────────────────────────────────────────
  
  const renderEditor = () => {
    return (
      <div style={{ position: 'relative' }}>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => handleMessageChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            width: '100%',
            minHeight: '120px',
            maxHeight: '400px',
            outline: 'none',
            resize: 'none',
            fontSize: '0.938rem',
            lineHeight: '1.5',
            color: 'var(--bs-body-color)',
            backgroundColor: 'transparent',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            padding: '12px',
            borderRadius: '8px',
            border: `2px solid ${
              error ? '#F2545B' :
              isFocused ? '#2ca799' :
              'var(--bs-border-color)'
            }`,
            transition: 'border-color 0.3s ease'
          }}
        />
        
        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '12px'
        }}>
          {/* Emoji picker inline */}
          {showEmojiPicker && (
            <div style={{
              position: 'absolute',
              bottom: '60px',
              left: '0',
              backgroundColor: 'var(--bs-body-bg)',
              border: '1px solid var(--bs-border-color)',
              borderRadius: '8px',
              padding: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              maxWidth: '300px',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: '4px'
              }}>
                {EMOJI_LIST.map((emoji, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleInsertEmoji(emoji)}
                    style={{
                      padding: '8px',
                      border: 'none',
                      background: 'transparent',
                      fontSize: '1.2rem',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(44, 167, 153, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* Character counter */}
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: isTooLong 
              ? '#F2545B' 
              : isTooShort 
              ? '#FFB84D' 
              : charPercentage > 80 
              ? '#FF7A59' 
              : 'var(--bs-secondary)'
          }}>
            {stats.characterCount} / {maxLength}
            {isTooShort && (
              <span style={{ marginLeft: '8px', fontSize: '0.688rem' }}>
                (mín. {minLength})
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: PREVIEW WHATSAPP
  // ───────────────────────────────────────────────────────────────────────
  
  const renderPreview = () => {
    if (!showPreview) return null;
    
    return (
      <Card style={{
        border: '1px solid var(--bs-border-color)',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        backgroundColor: 'var(--bs-body-bg)',
        marginBottom: '16px'
      }}>
        <Card.Header style={{
          backgroundColor: 'var(--bs-body-bg)',
          borderBottom: '1px solid var(--bs-border-color)',
          padding: '12px 16px'
        }}>
          <div style={{
            fontSize: '0.875rem',
            fontWeight: 600,
            color: 'var(--bs-body-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FontAwesomeIcon icon={faMessage} style={{ color: '#2ca799' }} />
            Preview WhatsApp
          </div>
        </Card.Header>
        <Card.Body style={{
          padding: '20px',
          backgroundColor: '#ECE5DD',
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)',
          minHeight: '200px'
        }}>
          {previewMessage ? (
            <div>
              {/* Burbuja WhatsApp */}
              <div style={{
                backgroundColor: '#DCF8C6',
                padding: '10px 14px',
                borderRadius: '8px',
                maxWidth: '80%',
                marginLeft: 'auto',
                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                position: 'relative',
                marginBottom: '8px'
              }}>
                <div style={{
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  color: '#000',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {previewMessage}
                </div>
                <div style={{
                  fontSize: '0.688rem',
                  color: '#667781',
                  textAlign: 'right',
                  marginTop: '4px'
                }}>
                  {new Date().toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              
              {/* Contador adicional */}
              {contactCount > 1 && (
                <div style={{
                  fontSize: '0.75rem',
                  color: '#667781',
                  textAlign: 'center',
                  marginTop: '12px'
                }}>
                  + {contactCount - 1} destinatario{contactCount - 1 > 1 ? 's' : ''} más
                </div>
              )}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              color: 'var(--bs-secondary)',
              fontSize: '0.875rem',
              padding: '40px 20px'
            }}>
              Escribe un mensaje para ver el preview
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: VARIABLE MODAL
  // ───────────────────────────────────────────────────────────────────────
  
  const renderVariableModal = () => {
    return (
      <Modal
        show={showVariableModal}
        onHide={() => setShowVariableModal(false)}
        centered
        size="lg"
        style={{ zIndex: 999999 }}
        backdropClassName="modal-backdrop-high"
      >
        <Modal.Header closeButton style={{
          borderBottom: '1px solid var(--bs-border-color)',
          padding: '16px 20px'
        }}>
          <Modal.Title style={{ fontSize: '1.125rem', fontWeight: 600 }}>
            <FontAwesomeIcon icon={faLightbulb} className="me-2" style={{ color: '#2ca799' }} />
            Insertar Variable
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '20px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '12px'
          }}>
            {enabledVariables.map(variable => {
              const isUsed = usedVariables.some(v => v.id === variable.id);
              
              return (
                <button
                  key={variable.id}
                  onClick={() => handleInsertVariable(variable)}
                  style={{
                    padding: '16px',
                    border: `2px solid ${isUsed ? '#2ca799' : 'var(--bs-border-color)'}`,
                    borderRadius: '8px',
                    background: isUsed ? 'rgba(44, 167, 153, 0.1)' : 'transparent',
                    color: 'var(--bs-body-color)',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    textAlign: 'left'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(44, 167, 153, 0.1)';
                    e.currentTarget.style.borderColor = '#2ca799';
                  }}
                  onMouseLeave={(e) => {
                    if (!isUsed) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = 'var(--bs-border-color)';
                    }
                  }}
                >
                  <div style={{
                    color: '#2ca799',
                    fontSize: '0.813rem',
                    fontFamily: 'monospace',
                    marginBottom: '4px',
                    fontWeight: 600
                  }}>
                    {variable.placeholder}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: 'var(--bs-body-color)',
                    marginBottom: '4px'
                  }}>
                    {variable.name}
                  </div>
                  <div style={{
                    fontSize: '0.688rem',
                    color: 'var(--bs-secondary)',
                    fontStyle: 'italic'
                  }}>
                    Ej: {variable.example}
                  </div>
                </button>
              );
            })}
          </div>
          
          {enabledVariables.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: 'var(--bs-secondary)'
            }}>
              No hay variables habilitadas
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{
          borderTop: '1px solid var(--bs-border-color)',
          padding: '12px 20px'
        }}>
          <Button
            variant="outline-secondary"
            onClick={() => setShowVariableModal(false)}
            size="sm"
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: LINK MODAL
  // ───────────────────────────────────────────────────────────────────────
  
  const renderLinkModal = () => {
    return (
      <Modal
        show={showLinkModal}
        onHide={() => setShowLinkModal(false)}
        centered
        style={{ zIndex: 999999 }}
        backdropClassName="modal-backdrop-high"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.125rem', fontWeight: 600 }}>
            <FontAwesomeIcon icon={faLink} className="me-2" style={{ color: '#2ca799' }} />
            Insertar Link
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '0.875rem', fontWeight: 500 }}>
              Texto del link (opcional)
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Haz clic aquí"
              value={linkText}
              onChange={(e) => setLinkText(e.target.value)}
              style={{ fontSize: '0.875rem' }}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label style={{ fontSize: '0.875rem', fontWeight: 500 }}>
              URL *
            </Form.Label>
            <Form.Control
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              style={{ fontSize: '0.875rem' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowLinkModal(false)}
            size="sm"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleInsertLink}
            disabled={!linkUrl}
            size="sm"
            style={{
              backgroundColor: '#2ca799',
              borderColor: '#2ca799'
            }}
          >
            <FontAwesomeIcon icon={faCheck} className="me-1" />
            Insertar
          </Button>
        </Modal.Footer>
      </Modal>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: TEMPLATE MODAL (Custom con Portal)
  // ───────────────────────────────────────────────────────────────────────
  
  const renderTemplateModal = () => {
    if (!showTemplateModal) return null;
    
    const modalContent = (
      <>
        {/* Backdrop */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: modalZIndex,
            animation: 'modalBackdropFadeIn 0.2s ease-out'
          }}
          onClick={() => setShowTemplateModal(false)}
        />
        
        {/* Modal */}
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: modalZIndex + 1,
            width: '90%',
            maxWidth: '900px',
            maxHeight: '90vh',
            backgroundColor: 'var(--bs-body-bg)',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            animation: 'modalSlideIn 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
            display: 'flex',
            flexDirection: 'column'
          }}
          onClick={(e) => {
            e.stopPropagation();
            bringModalToFront();
          }}
        >
          {/* Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--bs-border-color)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'var(--bs-body-bg)',
            flexShrink: 0,
            borderRadius: '12px 12px 0 0'
          }}>
            <h5 style={{ margin: 0, fontWeight: 600, fontSize: '1.25rem' }}>
              <FontAwesomeIcon icon={faFileAlt} className="me-2" style={{ color: '#2ca799' }} />
              Seleccionar Template
            </h5>
            <button
              onClick={() => setShowTemplateModal(false)}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '1.5rem',
                color: 'var(--bs-secondary)',
                cursor: 'pointer',
                padding: '0',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                e.currentTarget.style.color = 'var(--bs-body-color)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--bs-secondary)';
              }}
            >
              ×
            </button>
          </div>
          
          {/* Category Filter */}
          <div style={{ 
            padding: '16px 24px', 
            borderBottom: '1px solid var(--bs-border-color)',
            backgroundColor: 'var(--bs-body-bg)',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <Badge
                bg={selectedCategory === 'all' ? 'primary' : 'light'}
                text={selectedCategory === 'all' ? 'white' : 'dark'}
                style={{
                  padding: '8px 16px',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  backgroundColor: selectedCategory === 'all' ? '#2ca799' : undefined,
                  borderColor: selectedCategory === 'all' ? '#2ca799' : undefined
                }}
                onClick={() => setSelectedCategory('all')}
              >
                Todos ({templates.length})
              </Badge>
              {(categories || []).map(cat => {
                const count = templates.filter(t => t.category === cat).length;
                return (
                  <Badge
                    key={cat}
                    bg={selectedCategory === cat ? 'primary' : 'light'}
                    text={selectedCategory === cat ? 'white' : 'dark'}
                    style={{
                      padding: '8px 16px',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      borderRadius: '8px',
                      transition: 'all 0.2s ease',
                      textTransform: 'capitalize',
                      backgroundColor: selectedCategory === cat ? '#2ca799' : undefined,
                      borderColor: selectedCategory === cat ? '#2ca799' : undefined
                    }}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat} ({count})
                  </Badge>
                );
              })}
            </div>
          </div>
          
          {/* Body - Template List */}
          <div style={{ 
            padding: '24px', 
            overflowY: 'auto',
            flex: 1
          }}>
            {loadingTemplates ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px'
              }}>
                <Spinner animation="border" style={{ color: '#2ca799' }} />
                <p style={{ margin: 0, color: 'var(--bs-secondary)' }}>
                  Cargando templates...
                </p>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px',
                color: 'var(--bs-secondary)'
              }}>
                <FontAwesomeIcon icon={faFileAlt} size="3x" style={{ marginBottom: '16px', opacity: 0.3 }} />
                <p style={{ fontSize: '1rem', marginBottom: '8px' }}>
                  No hay templates disponibles
                </p>
                <small style={{ fontSize: '0.85rem' }}>
                  {selectedCategory === 'all' 
                    ? 'Crea tu primer template en la pestaña Templates'
                    : 'No hay templates en esta categoría'}
                </small>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                gap: '16px'
              }}>
                {filteredTemplates.map(template => (
                  <div
                    key={template.id}
                    onClick={() => handleLoadTemplate(template)}
                    style={{
                      padding: '16px',
                      border: '1px solid var(--bs-border-color)',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: 'var(--bs-body-bg)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#2ca799';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(44, 167, 153, 0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--bs-border-color)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {/* Header */}
                    <div style={{ marginBottom: '12px' }}>
                      <h6 style={{ 
                        margin: 0, 
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        marginBottom: '6px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {template.name}
                      </h6>
                      <Badge 
                        bg="light" 
                        text="dark" 
                        style={{ 
                          fontSize: '0.7rem',
                          padding: '4px 8px',
                          textTransform: 'capitalize',
                          fontWeight: 500
                        }}
                      >
                        <FontAwesomeIcon icon={faTag} className="me-1" style={{ fontSize: '0.65rem' }} />
                        {template.category}
                      </Badge>
                    </div>
                    
                    {/* Content Preview */}
                    <p style={{ 
                      fontSize: '0.8rem',
                      color: 'var(--bs-secondary)',
                      marginBottom: '12px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.4'
                    }}>
                      {template.content}
                    </p>
                    
                    {/* Footer Stats */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '0.7rem',
                      color: 'var(--bs-secondary)',
                      paddingTop: '8px',
                      borderTop: '1px solid rgba(0,0,0,0.06)'
                    }}>
                      <span>
                        <FontAwesomeIcon icon={faChartLine} className="me-1" />
                        {template.metadata?.usageCount || 0} usos
                      </span>
                      {template.variables && template.variables.length > 0 && (
                        <span>
                          {template.variables.length} variable{template.variables.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        <style>{`
          @keyframes modalBackdropFadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes modalSlideIn {
            from {
              opacity: 0;
              transform: translate(-50%, -50%) scale(0.9) translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translate(-50%, -50%) scale(1) translateY(0);
            }
          }
        `}</style>
      </>
    );
    
    return ReactDOM.createPortal(modalContent, document.body);
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: NOTIFICATIONS
  // ───────────────────────────────────────────────────────────────────────
  
  const renderNotifications = () => {
    if (notifications.length === 0) return null;
    
    const getColorByType = (type: Notification['type']) => {
      switch (type) {
        case 'success': return { bg: '#10b981', border: '#059669' };
        case 'error': return { bg: '#ef4444', border: '#dc2626' };
        case 'warning': return { bg: '#f59e0b', border: '#d97706' };
        case 'info': return { bg: '#3b82f6', border: '#2563eb' };
      }
    };
    
    return (
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        maxWidth: '400px'
      }}>
        {notifications.map(notification => {
          const colors = getColorByType(notification.type);
          return (
            <div
              key={notification.id}
              style={{
                backgroundColor: colors.bg,
                border: `2px solid ${colors.border}`,
                borderRadius: '12px',
                padding: '16px 20px',
                color: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                fontSize: '0.95rem',
                fontWeight: 500,
                animation: 'slideInRight 0.3s ease-out',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px'
              }}
            >
              <span>{notification.message}</span>
              <button
                onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  padding: 0,
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  opacity: 0.8,
                  transition: 'opacity 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
              >
                ×
              </button>
            </div>
          );
        })}
        
        <style>{`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}</style>
      </div>
    );
  };
  
  // ───────────────────────────────────────────────────────────────────────
  // RENDER: MAIN
  // ───────────────────────────────────────────────────────────────────────
  
  return (
    <div className={className}>
      <Row className="g-4">
        {/* Editor Column */}
        <Col lg={8}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            backgroundColor: 'var(--bs-body-bg)'
          }}>
            <Card.Header style={{
              backgroundColor: 'var(--bs-body-bg)',
              borderBottom: '1px solid var(--bs-border-color)',
              padding: '16px 20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--bs-body-color)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <FontAwesomeIcon icon={faPen} style={{ color: '#2ca799' }} />
                Editor de Mensaje
                {contactCount > 0 && (
                  <Badge
                    bg="light"
                    text="dark"
                    style={{
                      fontSize: '0.75rem',
                      padding: '4px 8px',
                      backgroundColor: 'rgba(44, 167, 153, 0.1)',
                      color: '#2ca799'
                    }}
                  >
                    {contactCount} destinatarios
                  </Badge>
                )}
              </div>
              
              {renderRiskIndicator()}
            </Card.Header>
            <Card.Body style={{ padding: '24px' }}>
              {renderToolbar()}
              {renderEditor()}
              
              {/* Risk warnings */}
              {riskData && riskData.mainIssues && riskData.mainIssues.length > 0 && (
                <Card style={{
                  marginTop: '16px',
                  border: `1px solid ${RISK_COLORS[riskData.level]}40`,
                  borderRadius: '8px',
                  backgroundColor: `${RISK_COLORS[riskData.level]}10`
                }}>
                  <Card.Body style={{ padding: '12px' }}>
                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: RISK_COLORS[riskData.level],
                      marginBottom: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <FontAwesomeIcon icon={RISK_ICONS[riskData.level]} />
                      Recomendaciones de seguridad
                    </div>
                    <ul style={{
                      margin: 0,
                      paddingLeft: '20px',
                      fontSize: '0.75rem',
                      color: 'var(--bs-body-color)',
                      lineHeight: '1.6'
                    }}>
                      {riskData.mainIssues.map((issue, idx) => (
                        <li key={idx}>{issue}</li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              )}
              
              {/* Error */}
              {error && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  backgroundColor: 'rgba(242, 84, 91, 0.1)',
                  border: '1px solid rgba(242, 84, 91, 0.3)',
                  borderRadius: '8px',
                  color: '#F2545B',
                  fontSize: '0.813rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {error}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        {/* Sidebar Column */}
        <Col lg={4}>
          {/* Preview WhatsApp */}
          {renderPreview()}
          
          {/* Templates Button */}
          {showTemplates && (
            <Button
              variant="outline-primary"
              className="w-100 mb-3"
              onClick={handleOpenTemplateSelector}
              style={{
                borderRadius: '8px',
                fontWeight: 500,
                padding: '12px',
                borderColor: '#2ca799',
                color: '#2ca799'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#2ca799';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#2ca799';
              }}
            >
              <FontAwesomeIcon icon={faFileAlt} className="me-2" />
              Cargar desde Template
            </Button>
          )}
        </Col>
      </Row>
      
      {/* Modals */}
      {renderVariableModal()}
      {renderLinkModal()}
      {renderTemplateModal()}
      
      {/* Notifications */}
      {renderNotifications()}
      
      {/* Global styles for modal backdrops */}
      <style>{`
        .modal-backdrop-high {
          z-index: 999998 !important;
        }
        
        .modal {
          z-index: 999999 !important;
        }
      `}</style>
    </div>
  );
};

export default MessageComposer;
export { DEFAULT_VARIABLES };